/**
 * Pontos de empate entre consórcio e financiamento.
 *
 * Os juros do financiamento crescem com o prazo; a taxa de administração do
 * consórcio é fixa sobre o crédito. Em prazo curto o financiamento pode sair
 * mais barato — o estudo então mostra a partir de onde o consórcio ganha, em
 * vez de só um número negativo (pedido do Rick, 17/10):
 *
 *   - prazo de empate: menor prazo em que os juros passam a taxa;
 *   - taxa de empate: maior taxa de administração que ainda ganha neste prazo;
 *   - juros de empate: juros ao ano em que os dois custam o mesmo.
 *
 * Juros e taxa são proporcionais ao crédito, então tudo aqui é em % do crédito.
 */
import { calcularFinanciamento } from './comparativo.ts';

type Sistema = 'Price' | 'SAC';

/** Prazo máximo procurado (35 anos, o teto do financiamento imobiliário). */
export const PRAZO_MAX_EMPATE = 420;

/** Juros totais do financiamento, em % do crédito. */
export const jurosPctDoCredito = (prazo: number, jurosAa: number, sistema: Sistema) =>
  calcularFinanciamento(1, prazo, jurosAa, sistema).juros * 100;

/** Menor prazo (meses) em que os juros passam a taxa de administração; null se nem em 35 anos. */
export const prazoDeEmpate = (taxaAdminPct: number, jurosAa: number, sistema: Sistema): number | null => {
  if (!(jurosAa > 0)) return null;
  for (let n = 1; n <= PRAZO_MAX_EMPATE; n++) {
    if (jurosPctDoCredito(n, jurosAa, sistema) > taxaAdminPct) return n;
  }
  return null;
};

/** Juros ao ano em que financiamento e consórcio custam o mesmo, neste prazo. */
export const jurosDeEmpate = (prazo: number, taxaAdminPct: number, sistema: Sistema): number => {
  let lo = 0;
  let hi = 1000;
  for (let k = 0; k < 100; k++) {
    const meio = (lo + hi) / 2;
    if (jurosPctDoCredito(prazo, meio, sistema) < taxaAdminPct) lo = meio;
    else hi = meio;
  }
  return (lo + hi) / 2;
};

export type Empate = {
  sistema: Sistema;
  /** O consórcio já ganha no prazo do plano. */
  consorcioGanha: boolean;
  prazoMeses: number | null;
  taxaMaxPct: number;
  jurosEmpateAa: number;
  /** Juros em % do crédito por prazo (índice 0 = 1 mês), para o gráfico. */
  curva: number[];
};

export const calcularEmpate = (o: {
  prazo: number;
  taxaAdminPct: number;
  jurosAa: number;
  sistema: Sistema;
  /** Até que prazo vai a curva do gráfico. */
  ate: number;
}): Empate => {
  const taxaMaxPct = jurosPctDoCredito(o.prazo, o.jurosAa, o.sistema);
  return {
    sistema: o.sistema,
    consorcioGanha: taxaMaxPct > o.taxaAdminPct,
    prazoMeses: prazoDeEmpate(o.taxaAdminPct, o.jurosAa, o.sistema),
    taxaMaxPct,
    jurosEmpateAa: jurosDeEmpate(o.prazo, o.taxaAdminPct, o.sistema),
    curva: Array.from({ length: Math.max(2, o.ate) }, (_, k) => jurosPctDoCredito(k + 1, o.jurosAa, o.sistema)),
  };
};

/** Até onde o gráfico vai: passa do prazo do plano e do empate com folga, em anos cheios. */
export const horizonteDoGrafico = (prazo: number, prazoEmpate: number | null) => {
  const alvo = Math.max(prazo, prazoEmpate ?? 0) * 1.6;
  return Math.min(PRAZO_MAX_EMPATE, Math.max(36, Math.ceil(alvo / 12) * 12));
};
